import type { Partner, IntegrationTypes, OrganizationType, Theme, SamlProvider } from './etc';
import type { PlannedScheduledDuration } from '../vertices/properties/planned-scheduled-duration';
import type { MaximumScheduleSpan } from '../vertices/properties/maximum-schedule-span';
import type { PublicId } from '../vertices/properties';


type Identifier = {
  value:   string;
  type:    'VAT' | 'SchoolCode' | 'MunicipalityCode' | 'SchoolSoft' | 'Sdui' | 'Edlevo' | 'Edaktus';
  country: string;
};

export type CoplanUsageLimits = {
  /**
   * The period over which usage is measured.
   * - `'day'`   → rolling 24 h window (UTC calendar day)
   * - `'week'`  → rolling 7-day window
   * - `'month'` → rolling 30-day window
   */
  period: 'day' | 'week' | 'month';
  /** Maximum total cost in USD. Requests exceeding this are rejected with 429. */
  maxCostUSD: number;
};

type AppFeature = {
  substitutionPlanner?: {
    enabled: boolean;
  };
  statisticsModule?: {
    /**
     * Enables the whole statistics module.
     */
    enabled?: boolean;

    scb?:             boolean;
    lunchCongestion?: boolean;
  };
  coplan?: {
    enabled: boolean;
    limit?:   CoplanUsageLimits | null;
  };
  includeStepGuide?: boolean;
};

export type Address = {
  type:           string; // Type of address (e.g., home, work, etc.)
  streetAddress:  string; // Street address (e.g., 123 Main St)
  addressLine2?:  string; // Optional second line of the address (e.g., Apt 4B)sss
  addressLine3?:  string; // Optional third line of the address
  postalCode:     string; // Postal code (e.g., 12345)
  locality:       string; // City or locality (e.g., New York)
  state?:         string; // Optional state or province (e.g., NY)
  region?:        string; // Optional region (e.g., Northeast)
  country:        string; // Country (e.g., USA)
  geoCoordinates: {
    latitude:  string;
    longitude: string;
  };
};

export type TimeRosterSection = {
  start: string; // In HH:mm format
  end:   string; // In HH:mm format
  id:    string;
  name:  string;
};

export type PublicConfig = {
  filters: {
    groups:      boolean;
    teachers:    boolean;
    inLocations: boolean;
    tags:        boolean;
    subjects:    boolean;
    subGroups:   boolean;
  };
  showTeacherWorkingHours: boolean;
  multipleSelection:       boolean;
  timeFilters: {
    all:     boolean;
    periods: boolean;
  };
  lockedTimes: boolean;
};

export type AutoSyncConfig = {
  enabled:       boolean;
  integration?:  IntegrationTypes;
  weekdaysOnly?: boolean;
  time?:         string;
};

export const INACTIVITY_ACTIONS = {
  REFRESH: 'refresh',
  LOGOUT:  'logout',
} as const;
export type InactivityAction =
  (typeof INACTIVITY_ACTIONS)[keyof typeof INACTIVITY_ACTIONS];

export type InactivitySettings = {
  /**
   * In units of minutes
   */
  time:   number;
  action: InactivityAction;
};

export type GlobalDefaultValues = {
  dayStart:                             string;
  dayEnd:                               string;
  numDays:                              number;
  potentialCenter?:                     string;
  minimizeTeacherGaps?:                 boolean;
  defaultGroupMinimumBreakLength?:      number;
  defaultEventMinimumBreakLength?:      number;
  defaultDependencyMinimumBreakLength?: number;
  defaultPlannedScheduledDuration?:     PlannedScheduledDuration;
  defaultMaximumScheduleSpan?:          MaximumScheduleSpan;
  defaultEventDurationVariance?:        number;
};

export namespace Company {
  export type core = {
    readonly id:        PublicId;
    associatedPartner?: Partner | null;
    createdAt?:         string;
  };

  type optionals = {
    name?:       string;
    logo?:       string; // URL to the company's logo
    country?:    string;
    timezone?:   string;
    environment: {
      theme?:              Theme;
      organizationType?:   OrganizationType;
      appFeatures?:        AppFeature;
      canCreateCompanies?: boolean;
    };
    identifiers:      Identifier[];
    addresses:        Address[];
    constituentTypes: string[];
    samlProviders?:   SamlProvider[];
    publicSettings?:  PublicConfig;
    autoSync?:        AutoSyncConfig;
    organization?: {
      name?:               string;
      logo?:               string;
      country?:            string;
      inactivitySettings?: InactivitySettings;
      constituentTypes?:   string[];
      environment?:        Company.complete['environment'];
    };
    inactivitySettings?:  InactivitySettings;
    timeRoster?:          TimeRosterSection[];
    globalDefaultValues?: GlobalDefaultValues;
  };

  export type complete = core & optionals;
}
